import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createVideoRequest } from '../../../../services/lichvietvideo/videoServiceRequest';
import { uploadImageCDN } from '../../../../utils/utility';
import {
  getAllBlocksVideoRequest,
  getAllTagsVideoRequest,
  getCampaignListRequest, // Added campaign request
} from '../../../../services/lichvietvideo/commonServiceRequest';
import { CTA_TYPE_OBJ, CTA_TYPES } from '../config';

const { Text, Title } = Typography;

export const checkValidUpload = (file, MAX_SIZE_MB = 3) => {
  const isTooLarge = file.size / 1024 / 1024 > MAX_SIZE_MB;

  if (isTooLarge) {
    notification.error({
      message: 'Kích thước ảnh quá lớn!',
      description: `Vui lòng chọn ảnh nhỏ hơn ${MAX_SIZE_MB}MB.`,
    });
    return false;
  }
  return true;
};

export const renderCTAField = ({
  watchTypeCtaButton,
  handleUploadCtaImage,
  campaignOptions,
  handleCampaignChange,
  form,
}) => {
  // Added new props
  switch (watchTypeCtaButton) {
    case CTA_TYPE_OBJ.TEXT:
      return (
        <>
          <Col xs={24} md={12}>
            <Form.Item
              name="cta_name"
              label={<Text>CTA text</Text>}
              rules={[{ required: true, message: 'Vui lòng nhập cta text' }]}
            >
              <Input placeholder="Nhập cta text..." />
            </Form.Item>
          </Col>
          <Col xs={24} md={3}>
            <Form.Item label={<Text>Chiến dịch</Text>} name="campaign_id_text">
              <Select
                placeholder="Chọn chiến dịch"
                options={campaignOptions}
                onChange={(value) => handleCampaignChange(value, form)}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item
              name="cta_link"
              label={
                <Text>
                  CTA deeplink <span className="text-red-700">*</span>
                </Text>
              }
              rules={[
                { required: true, message: 'Vui lòng nhập cta deeplink' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const allowedSchemas = ['http', 'https', 'lichviet'];
                    try {
                      const url = new URL(value);
                      if (allowedSchemas.includes(url.protocol.replace(':', ''))) {
                        return Promise.resolve();
                      }
                    } catch {
                      if (value.startsWith('lichviet://')) return Promise.resolve();
                    }
                    return Promise.reject(new Error('Đường dẫn không hợp lệ!'));
                  },
                },
              ]}
            >
              <Input placeholder="Nhập cta deeplink..." />
            </Form.Item>
          </Col>
        </>
      );

    case CTA_TYPE_OBJ.IMAGE:
      return (
        <>
          <Col xs={24} md={12}>
            <Form.Item
              name="cta_image"
              label={<Text>CTA image</Text>}
              rules={[
                { required: true, message: 'Vui lòng nhập cta image link hoặc tải lên file' },
                { type: 'url', message: 'Đường dẫn không hợp lệ!' },
              ]}
            >
              <Input
                placeholder="Nhập URL hoặc tải lên file..."
                addonBefore={
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleUploadCtaImage}
                    className="hover:cursor-pointer"
                  >
                    <UploadOutlined />
                    Upload
                  </Upload>
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={3}>
            <Form.Item label={<Text>Chiến dịch</Text>} name="campaign_id_image">
              <Select
                placeholder="Chọn chiến dịch"
                options={campaignOptions}
                onChange={(value) => handleCampaignChange(value, form)}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item
              name="cta_link"
              label={
                <Text>
                  CTA deeplink <span className="text-red-700">*</span>
                </Text>
              }
              rules={[
                { required: true, message: 'Vui lòng nhập cta deeplink' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const allowedSchemas = ['http', 'https', 'lichviet'];
                    try {
                      const url = new URL(value);
                      if (allowedSchemas.includes(url.protocol.replace(':', ''))) {
                        return Promise.resolve();
                      }
                    } catch {
                      if (value.startsWith('lichviet://')) return Promise.resolve();
                    }
                    return Promise.reject(new Error('Đường dẫn không hợp lệ!'));
                  },
                },
              ]}
            >
              <Input placeholder="Nhập cta deeplink..." />
            </Form.Item>
          </Col>
        </>
      );

    default:
      return null;
  }
};

const CreateVideo = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchTypeCtaButton = Form.useWatch('cta_type', form);
  const [fileVideo, setFileVideo] = useState(null);
  const ctaLinkValue = Form.useWatch('cta_link', form);

  // get campaign list
  const { data: dataAllCampaigns } = useQuery({
    queryKey: ['dataAllCampaigns'],
    queryFn: () => getCampaignListRequest(),
  });

  useEffect(() => {
    if (ctaLinkValue && dataAllCampaigns?.length > 0) {
      try {
        const url = new URL(ctaLinkValue);
        const utmId = url.searchParams.get('utm_id');

        if (utmId) {
          const campaign = dataAllCampaigns.find((c) => String(c.id) === String(utmId));
          if (campaign) {
            form.setFieldsValue({
              campaign_id: campaign.id,
              campaign_id_text: campaign.name,
            });
          } else {
            form.setFieldsValue({
              campaign_id: undefined,
              campaign_id_text: undefined,
            });
          }
        } else {
          form.setFieldsValue({
            campaign_id: undefined,
            campaign_id_text: undefined,
          });
        }
      } catch (error) {
        // console.log('Not a valid URL, skipping campaign auto-selection.');
      }
    }
  }, [ctaLinkValue, dataAllCampaigns, form]);

  const dataAllCampaignsOption = useMemo(() => {
    return (
      dataAllCampaigns?.map((campaign) => ({
        label: campaign.name,
        value: campaign.id,
      })) || []
    );
  }, [dataAllCampaigns]);

  const updateQueryString = (queryString, key, value) => {
    const regex = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
    let qs = queryString;

    if (typeof value === 'undefined' || value === null || value === '') {
      // Remove the key if value is undefined/null/empty
      qs = qs.replace(regex, (match, p1, p2) => (p2 === '&' ? p1 : ''));
    } else if (qs.match(regex)) {
      // Replace existing key
      qs = qs.replace(regex, `$1${key}=${value}$2`);
    } else {
      // Add new key
      qs += (qs.length ? '&' : '') + `${key}=${value}`;
    }
    return qs.replace(/^&+|&+$/g, '');
  };

  const handleCampaignChange = (campaignId, formInstance) => {
    const currentCtaLink = formInstance.getFieldValue('cta_link') || '';

    const linkParts = currentCtaLink.split('?');
    const baseLink = linkParts[0];
    let queryString = linkParts.length > 1 ? linkParts.slice(1).join('?') : '';

    // Update utm_id
    queryString = updateQueryString(queryString, 'utm_id', campaignId || undefined);
    // Always set utm_source=video
    queryString = updateQueryString(queryString, 'utm_source', 'video');
    // Remove utm_source_id
    queryString = updateQueryString(queryString, 'utm_source_id', undefined);

    const newCtaLink = queryString ? `${baseLink}?${queryString}` : baseLink;

    formInstance.setFieldsValue({ cta_link: newCtaLink });
  };

  // get data blocks video
  const { data: dataAllBlocksVideo } = useQuery({
    queryKey: ['dataAllBlocksVideo'],
    queryFn: () => getAllBlocksVideoRequest(),
  });

  // get data tags video
  const { data: dataAllTagsVideo } = useQuery({
    queryKey: ['dataAllTagsVideo'],
    queryFn: () => getAllTagsVideoRequest(),
  });

  // convert list block video options
  const dataAllBlocksVideoOption = useMemo(() => {
    return dataAllBlocksVideo?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.name, value: item?.id };
      });
  }, [dataAllBlocksVideo]);

  // convert list miniapps options
  const dataAllTagsVideoOption = useMemo(() => {
    return dataAllTagsVideo?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.name, value: item?.id };
      });
  }, [dataAllTagsVideo]);

  // handle submit form
  const { mutate: mutateCreateVideo, isPending } = useMutation({
    mutationFn: createVideoRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Thêm mới video thành công!',
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      name: data?.name,
      description: data?.description,
      thumb: data?.thumb,
      short_video_link: data?.short_video_link,
      full_video_link: data?.full_video_link,
      publish_time: data?.publish_time ? data?.publish_time?.format('YYYY-MM-DD HH:mm:ss') : null,
      duration: data?.duration,
      block_ids: data?.block_ids || [],
      tag_ids: data?.tag_ids || [],
      cta_type: data?.cta_type || 0,
      cta_name: data?.cta_name || null,
      cta_image: data?.cta_image || null,
      cta_link: data?.cta_link,
      status: data?.status,
    };
    mutateCreateVideo(dataSubmit);
  };

  const handleUploadThumb = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) {
      return false;
    }
    const url = await uploadImageCDN(file, () => {}, 'lichviet_video');
    if (url) {
      notification.success({
        message: 'Tải lên thành công!',
      });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ thumb: safeUrl });
    }
    return false;
  };

  const handleUploadLinkVideoPreview = async (file) => {
    const isValid = checkValidUpload(file, 50);
    if (!isValid) {
      return false;
    }
    const url = await uploadImageCDN(file, () => {}, 'lichviet_video');
    if (url) {
      notification.success({
        message: 'Tải lên thành công!',
      });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ short_video_link: safeUrl });
    }
    return false;
  };

  const handleUploadLinkVideoFull = async (file) => {
    const isValid = checkValidUpload(file, 50);
    if (!isValid) {
      return false;
    }
    const url = await uploadImageCDN(file, () => {}, 'lichviet_video');
    if (url) {
      notification.success({
        message: 'Tải lên thành công!',
      });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ full_video_link: safeUrl });
    }
    return false;
  };

  const handleUploadCtaImage = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) {
      return false;
    }
    const url = await uploadImageCDN(file, () => {}, 'lichviet_video');
    if (url) {
      notification.success({
        message: 'Tải lên thành công!',
      });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ cta_image: safeUrl });
    }
    return false;
  };

  return (
    <Form
      name="create-video"
      form={form}
      layout="vertical"
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm video</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="name"
            label={
              <Text>
                Tên video <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập tên video' }]}
          >
            <Input placeholder="Nhập tên video..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="description"
            label={
              <Text>
                Mô tả <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập mô tả video' }]}
          >
            <Input placeholder="Nhập mô tả video..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="thumb"
            label={
              <Text>
                Ảnh thumb <span className="text-red-700">*</span>
              </Text>
            }
            rules={[
              { required: true, message: 'Vui lòng nhập hoặc tải lên file!' },
              { type: 'url', message: 'Đường dẫn không hợp lệ!' },
            ]}
          >
            <Input
              placeholder="Nhập URL hoặc tải lên file..."
              addonBefore={
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleUploadThumb}
                  className="hover:cursor-pointer"
                >
                  <UploadOutlined />
                  Upload
                </Upload>
              }
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="short_video_link"
            label={<Text>Link video preview</Text>}
            rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }]}
          >
            <Input
              placeholder="Nhập link short video hoặc tải lên file..."
              addonBefore={
                <Upload
                  accept="video/*"
                  showUploadList={false}
                  beforeUpload={handleUploadLinkVideoPreview}
                  className="hover:cursor-pointer"
                >
                  <UploadOutlined />
                  Upload
                </Upload>
              }
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="full_video_link"
            label={
              <Text>
                Link video <span className="text-red-700">*</span>
              </Text>
            }
            rules={[
              { required: true, message: 'Vui lòng nhập link video' },
              { type: 'url', message: 'Đường dẫn không hợp lệ!' },
            ]}
          >
            <Input
              placeholder="Nhập link video hoặc tải lên file video..."
              addonBefore={
                <Upload
                  accept="video/*"
                  showUploadList={false}
                  beforeUpload={handleUploadLinkVideoFull}
                  className="hover:cursor-pointer"
                >
                  <UploadOutlined />
                  Upload
                </Upload>
              }
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="block_ids" label={<Text>Khối</Text>}>
            <Select
              showSearch
              mode="multiple"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn khối"
              options={dataAllBlocksVideoOption}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="publish_time" label={<Text>Thời gian phát hành</Text>}>
            <DatePicker showTime placeholder="Chọn thời gian phát hành" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="duration"
            label={<Text>Thời lượng Video (Số giây)</Text>}
            initialValue={0}
            rules={[
              {
                validator: (_, value) => {
                  if (!value || /^[1-9]\d*$/.test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject('Thời lượng phải là số nguyên không âm!');
                },
              },
            ]}
          >
            <InputNumber placeholder="Nhập số giây" style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="tag_ids" label={<Text>Tag</Text>}>
            <Select
              showSearch
              mode="multiple"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn tag"
              options={dataAllTagsVideoOption}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="cta_type" label={<Text>CTA type</Text>} initialValue={CTA_TYPE_OBJ.NOTHING}>
            <Select placeholder="Chọn cta type" options={CTA_TYPES} />
          </Form.Item>
        </Col>

        {renderCTAField({
          watchTypeCtaButton,
          handleUploadCtaImage,
          campaignOptions: dataAllCampaignsOption,
          handleCampaignChange,
          form,
        })}

        <Col xs={12}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={1}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateVideo;
